import React from 'react';
import {createTheme, type ThemeOptions} from '@mui/material/styles';
import '@mui/x-date-pickers/themeAugmentation';
import {ctaFocusRing} from '../../common/constants/focusStyle';

export const CUSTOM_BREAKPOINTS = {
    values: {
        xs: 0,
        sm: 600,
        md: 828,
        lg: 1056,
        xl: 1392,
    },
} as const;

const baseTheme = createTheme({
                                  breakpoints: CUSTOM_BREAKPOINTS,
                              });

const breakpoints = baseTheme.breakpoints;

const portalTheme: ThemeOptions = {
    breakpoints,
    palette: {
        mode: 'light',
        primary: {
            main: '#3B8511',
            dark: '#275e18',
            light: '#C7DEAE',
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: '#0076B9',
            dark: '#005284',
            light: '#005284',
            contrastText: '#FFFFFF',
        },
        tertiary: {
            main: '#FFDD00',
            contrastText: '#4A3F00',
            dark: '#E1BC06',
        },
        footerLink: {
            main: '#275e18',
        },
        error: {
            main: '#d32f2f',
            dark: '#c62828',
            light: '#ef5350',
            contrastText: '#FFFFFF',
        },
        action: {
            active: '#333333',
            hover: 'rgba(0, 0, 0, 0.04)',
            selected: 'rgba(0, 0, 0, 0.08)',
        },
        background: {
            default: '#ffffff',
            paper: '#ffffff',
        },
        text: {
            primary: '#333333',
            secondary: '#999999',
        },
        contrastThreshold: 4.5,
    },

    spacing: 4,

    typography: {
        fontFamily: '"HelveticaNeueLTStd-Roman", Helvetica, Arial, sans-serif',
        fontSize: 14,
        button: {
            textTransform: 'none',
        },

        h1: {
            fontSize: '3rem',
            fontFamily: 'var(--font-family-bold)',
            fontWeight: '700',
            letterSpacing: '-0.09375rem',
            lineHeight: '120%',
            [breakpoints.down('lg')]: {
                fontSize: '2.75rem',
                letterSpacing: '-0.0625rem',
            },
            [breakpoints.down('sm')]: {
                fontSize: '2rem',
                letterSpacing: '-0.0625rem',
            },
        },

        h2: {
            fontSize: '2.75rem',
            fontFamily: 'var(--font-family-bold)',
            fontWeight: '700',
            letterSpacing: '-0.03125rem',
            lineHeight: '120%',
            [breakpoints.only('md')]: {
                fontSize: '2.25rem',
            },
            [breakpoints.down('md')]: {
                fontSize: '1.75rem',
            },
        },

        h3: {
            fontSize: '2rem',
            fontFamily: 'var(--font-family-bold)',
            fontWeight: '700',
            letterSpacing: '0px',
            lineHeight: '120%',
            [breakpoints.only('md')]: {
                fontSize: '1.75rem',
            },
            [breakpoints.down('md')]: {
                fontSize: '1.5rem',
            },
        },

        h4: {
            fontSize: '1.25rem',
            fontFamily: 'var(--font-family-bold)',
            fontWeight: '700',
            letterSpacing: '0.01563rem',
            lineHeight: '125%',
        },

        h5: {
            fontSize: '1.25rem',
            fontFamily: 'var(--font-family-bold)',
            fontWeight: '700',
            letterSpacing: '0.01563rem',
            lineHeight: '125%',
        },

        h6: {
            fontSize: '1.25rem',
            fontFamily: 'var(--font-family-bold)',
            fontWeight: '700',
            letterSpacing: '0.01563rem',
            lineHeight: '160%',
        },

        subtitle1: {
            fontSize: '1rem',
            fontWeight: '400',
            letterSpacing: '0.00938rem',
            lineHeight: '175%',
        },

        subtitle2: {
            fontSize: '0.875rem',
            fontWeight: '400',
            letterSpacing: '0.1px',
            lineHeight: '157%',
        },

        body1: {
            fontSize: '1.125rem',
            fontWeight: '400',
            letterSpacing: '0.15px',
            lineHeight: '150%',
            [breakpoints.down('md')]: {
                fontSize: '1rem',
            },
        },

        body2: {
            fontSize: '1rem',
            fontWeight: '400',
            letterSpacing: '0.17px',
            lineHeight: '143%',
        },

        caption: {
            fontSize: '0.75rem',
            fontWeight: '400',
            letterSpacing: '0.4px',
            lineHeight: '28px',
        },

        overline: {
            fontSize: '0.75rem',
            fontWeight: '450',
            letterSpacing: '0.35px',
            lineHeight: '18px',
        },

        ctaButton: {
            fontSize: '15px',
            fontFamily: '"HelveticaNeueLTStd-Md", Helvetica, Arial, sans-serif',
            lineHeight: '26px',
        },

        navigationItemDense: {
            fontSize: '14px',
            letterSpacing: '0.17px',
            lineHeight: '143%',
            fontFamily: '"HelveticaNeueLTStd-Md", Helvetica, Arial, sans-serif',
            fontWeight: '500',
        },

        accordion: {
            fontSize: '18px',
            lineHeight: '130%',
            fontFamily: '"HelveticaNeueLTStd-Md", Helvetica, Arial, sans-serif',
            fontWeight: '500',
            letterSpacing: '0.15px',
        },

        navigationItem: {
            fontSize: '16px',
            fontStyle: 'normal',
            fontFamily: '"HelveticaNeueLTStd-Md", Helvetica, Arial, sans-serif',
            fontWeight: '500',
            lineHeight: '150%',
            letterSpacing: '0.15px',
        },

        linkLabel: {
            fontFamily: 'var(--font-family-bold)',
            letterSpacing: '0.15px',
            lineHeight: '150%',
            textDecoration: 'none',
            fontSize: '18px',
            [breakpoints.down('md')]: {
                fontSize: '1rem',
            },
            fontWeight: '450',
            '&:hover': {
                textDecoration: 'underline',
                textDecorationThickness: '2px',
                textUnderlineOffset: '4px',
            },
        },

        blockQuote: {
            textAlign: 'center',
            fontSize: '40px',
            fontStyle: 'normal',
            fontWeight: 450,
            lineHeight: '140%',
        },

        landingPageTitle: {
            fontFamily: 'AsapCondensed-ExtraBold',
            fontWeight: 800
        },

        helveticaPageTitle: {
            fontFamily: 'HelveticaNeueLTProBlkCn',
            fontWeight: 900,
            letterSpacing: 0,
            lineHeight: 1.17,
            fontSize: '28px',
            [breakpoints.up('sm')]: {
                fontSize: '40px',
                lineHeight: 1.08,
            },
            [breakpoints.up('md')]: {
                fontSize: '48px',
                lineHeight: 1,
            },
            [breakpoints.up('lg')]: {
                fontSize: '56px',
                lineHeight: 1.2,
            },
            [breakpoints.up('xl')]: {
                fontSize: '80px',
                lineHeight: 1.2,
            },
        },

        h3Part: {
            fontSize: '2rem',
            [breakpoints.only('md')]: {
                fontSize: '1.75rem',
            },
            [breakpoints.down('md')]: {
                fontSize: '1.5rem',
            },
        },
    },

    components: {
        MuiLink: {
            defaultProps: {
                underline: 'none',
            },
            styleOverrides: {
                // fontSize/fontWeight lagen bis MUI v8 als System-Props in defaultProps.
                // v9 hat die System-Props an Link entfernt, deshalb stehen sie jetzt hier.
                root: ({theme}) => ({
                    fontWeight: '450',
                    [theme.breakpoints.up('md')]: {
                        fontSize: '16px',
                    },
                    [theme.breakpoints.up('lg')]: {
                        fontSize: '18px',
                    },
                    textDecoration: 'underline',
                    textDecorationThickness: '2px',
                    textUnderlineOffset: '4px',
                    '&:hover': {
                        textDecoration: 'none',
                    },
                }),
            },
        },

        MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
            },
        },

        MuiButton: {
            defaultProps: {
                style: {
                    borderRadius: 999,
                    boxShadow: 'none',
                },
            },
            styleOverrides: {
                root: ctaFocusRing('&:focus-visible::after'),
            },
        },

        MuiCheckbox: {
            styleOverrides: {
                root: {
                    position: 'relative',
                    '& input:focus-visible + svg': {
                        outline: '2px solid var(--focus-state-blue)',
                        outlineOffset: '2px',
                        borderRadius: 2,
                    },
                },
            },
        },

        MuiRadio: {
            styleOverrides: {
                root: {
                    position: 'relative',
                    '& input:focus-visible + span': {
                        outline: '2px solid var(--focus-state-blue)',
                        outlineOffset: '2px',
                        borderRadius: 2,
                    },
                },
            },
        },

        MuiCard: {
            defaultProps: {
                style: {
                    borderRadius: 0,
                },
            },
        },

        MuiAccordion: {
            styleOverrides: {
                root: ({theme}) => ({
                    '&:before': {
                        display: 'none',
                        backgroundColor: 'transparent !important',
                    },
                    '& .Mui-expanded .MuiAccordionSummary-content p, .Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
                        color: theme.palette.primary.main,
                    },
                }),
            },
            defaultProps: {
                style: {
                    borderBottom: '0px solid #333333',
                    borderTop: '1px solid #e0e0e0',
                    borderRadius: '0',
                    boxShadow: 'none',
                },
            },
        },

        MuiInput: {
            defaultProps: {
                style: {
                    fontFamily: 'Helvetica Neue LT',
                    fontSize: '1rem',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '150%',
                    letterSpacing: '0.15px',
                },
            },
        },

        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: '#333333',
                    '&.Mui-focused': {
                        color: 'primary.main',
                    },
                },
            },
        },

        MuiOutlinedInput: {
            styleOverrides: {
                notchedOutline: {
                    borderColor: '#333',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                },
                root: ({theme}) => ({
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#333',
                        borderWidth: '2px',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: '2px',
                    },
                }),
            },
        },

        // Ab MUI X v8 rendern die Date-Pickers PickersOutlinedInput statt OutlinedInput.
        // Die Regeln oben greifen dort nicht mehr und werden deshalb gespiegelt.
        MuiPickersOutlinedInput: {
            styleOverrides: {
                notchedOutline: {
                    borderColor: '#333',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                },
                root: ({theme}) => ({
                    '&:hover .MuiPickersOutlinedInput-notchedOutline': {
                        borderColor: '#333',
                        borderWidth: '2px',
                    },
                    '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: '2px',
                    },
                }),
            },
        },

        MuiListItem: {
            defaultProps: {
                style: {
                    fontSize: '1rem',
                    padding: 2,
                },
            },
        },

        MuiTableBody: {
            styleOverrides: {
                root: {
                    [breakpoints.down('md')]: {
                        'table.styled-table:not(.keep-orientation) &': {
                            display: 'flex',
                            overflow: 'auto',
                        },
                    },
                },
            },
        },

        MuiTableRow: {
            styleOverrides: {
                root: {
                    [breakpoints.down('md')]: {
                        'table.styled-table:not(.keep-orientation) &:first-of-type': {
                            position: 'sticky',
                            left: 0,
                        },
                        'table.styled-table:not(.keep-orientation) &': {
                            display: 'flex',
                            flexDirection: 'column',
                            minWidth: 'min-content',
                            flexShrink: 0,
                        },
                    },
                },
            },
        },

        MuiTable: {
            styleOverrides: {
                root: {
                    '&.styled-table': {
                        [breakpoints.up('md')]: {
                            '& tr:last-of-type > td': {
                                borderBottom: 0,
                            },
                            borderSpacing: '24px 8px',
                            '&:not(:has(td.fit-to-content))': {
                                tableLayout: 'fixed',
                            },
                            borderCollapse: 'separate',
                        },
                        [breakpoints.down('md')]: {
                            display: 'block',
                        },
                    },
                    '&.keep-orientation': {
                        [breakpoints.down('md')]: {
                            '& tr:last-of-type > td': {
                                borderBottom: 0,
                            },
                            borderSpacing: '24px 8px',
                            '&:not(:has(td.fit-to-content))': {
                                tableLayout: 'fixed',
                            },
                            borderCollapse: 'separate',
                            overflow: 'auto',
                        },
                    },
                },
            },
        },

        MuiTableCell: {
            styleOverrides: {
                head: ({theme}) => ({
                    'table.styled-table &': {
                        fontWeight: 500,
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        borderBottom: 0,
                        height: '55px',
                        [breakpoints.down('md')]: {
                            width: '160px',
                        },
                    },
                }),
                root: {
                    'table:not(.styled-table) &': {
                        padding: 0,
                        borderBottom: 0,
                        lineHeight: '100%',
                    },
                    'table.styled-table &.fit-to-content': {
                        [breakpoints.up('md')]: {
                            width: '0 !important',
                            wordBreak: 'auto-phrase',
                        },
                    },
                    'table.styled-table &': {
                        padding: '8px 12px',
                        lineHeight: '125%',
                        letterSpacing: '0.048px',
                        verticalAlign: 'middle',
                        [breakpoints.up('md')]: {
                            height: '44px',
                        },
                        [breakpoints.down('md')]: {
                            marginBottom: '12px',
                            marginRight: '24px',
                            alignContent: 'center',
                            height: '55px',
                        },
                        borderBottom: '1px solid #949494',
                        '& > a': {
                            textDecoration: 'none',
                            fontWeight: 400,
                            fontFamily: 'var(--font-family-normal)',
                        },
                    },
                },
            },
        },
    },
};

declare module '@mui/material/styles' {
    interface TypographyVariants {
        accordion: React.CSSProperties;
        navigationItemDense: React.CSSProperties;
        navigationItem: React.CSSProperties;
        ctaButton: React.CSSProperties;
        landingPageTitle: React.CSSProperties;
        helveticaPageTitle: React.CSSProperties;
        h3Part: React.CSSProperties;
    }

    interface TypographyVariantsOptions {
        accordion?: React.CSSProperties;
        navigationItemDense?: React.CSSProperties;
        navigationItem?: React.CSSProperties;
        ctaButton?: React.CSSProperties;
        landingPageTitle?: React.CSSProperties;
        helveticaPageTitle?: React.CSSProperties;
        h3Part?: React.CSSProperties;

        linkLabel?: React.CSSProperties | any;
        blockQuote?: React.CSSProperties | any;
    }

    interface Palette {
        tertiary?: {
            main: string;
            dark?: string;
            light?: string;
            contrastText: string;
        };
        footerLink: {
            main: string;
        };
    }

    interface PaletteOptions {
        tertiary?: {
            main: string;
            dark?: string;
            light?: string;
            contrastText: string;
        };
        footerLink?: {
            main: string;
        };
    }
}

declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        navigationItemDense: true;
        accordion: true;
        navigationItem: true;
        linkLabel: true;
        blockQuote: true;
        ctaButton: true;
        landingPageTitle: true;
        helveticaPageTitle: true;
        h3Part: true;
    }
}

export const theme = createTheme(baseTheme, portalTheme);

export default portalTheme;
